import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import FilterComponent from "./FilterComponent";
import DataTable from "./DataTable";
import { PATIENTS_LIST } from "../constants/api";
import {
  PAGINATION,
  MESSAGES,
  FILTER_OPTIONS,
  DEFAULT_PATIENT_SORT,
  SORT_OPTIONS,
  PATIENT_LIST_QUERY_KEYS,
  PATIENT_LIST_SEARCH_DEBOUNCE_MS,
} from "../constants/app";
import { PageLoading } from "./loading/LoadingIndicators";
import { apiUtils, errorUtils } from "../utils";
import type { Patient, PaginationState, PatientListQuery } from "../types";

const VALID_SORT_FIELDS = new Set<string>([
  SORT_OPTIONS.FIELDS.NAME,
  SORT_OPTIONS.FIELDS.AGE,
  SORT_OPTIONS.FIELDS.LAST_SYNC,
  SORT_OPTIONS.FIELDS.RISK_LEVEL,
  SORT_OPTIONS.FIELDS.CREATED_AT,
  SORT_OPTIONS.FIELDS.UPDATED_AT,
]);
const VALID_DIRECTIONS = new Set<PatientListQuery["dir"]>([
  SORT_OPTIONS.DIRECTIONS.ASC,
  SORT_OPTIONS.DIRECTIONS.DESC,
]);
const VALID_RISK = new Set<string>([
  FILTER_OPTIONS.ALL,
  FILTER_OPTIONS.RISK_LEVELS.POOR,
  FILTER_OPTIONS.RISK_LEVELS.AVERAGE,
  FILTER_OPTIONS.RISK_LEVELS.GOOD,
]);
const VALID_AGE = new Set<string>([
  FILTER_OPTIONS.ALL,
  ...Object.values(FILTER_OPTIONS.AGE_GROUPS),
]);
const VALID_GENDER = new Set<string>([
  FILTER_OPTIONS.ALL,
  ...Object.values(FILTER_OPTIONS.GENDERS),
]);

const parsePatientListParams = (searchParams: URLSearchParams): PatientListQuery => {
  const search = searchParams.get(PATIENT_LIST_QUERY_KEYS.SEARCH) ?? "";
  const riskValue = searchParams.get(PATIENT_LIST_QUERY_KEYS.RISK) ?? FILTER_OPTIONS.ALL;
  const ageValue = searchParams.get(PATIENT_LIST_QUERY_KEYS.AGE) ?? FILTER_OPTIONS.ALL;
  const genderValue = searchParams.get(PATIENT_LIST_QUERY_KEYS.GENDER) ?? FILTER_OPTIONS.ALL;
  const sortValue = searchParams.get(PATIENT_LIST_QUERY_KEYS.SORT) ?? DEFAULT_PATIENT_SORT.field;
  const dirValue = searchParams.get(PATIENT_LIST_QUERY_KEYS.DIR) ?? DEFAULT_PATIENT_SORT.direction;
  const pageRaw = parseInt(
    searchParams.get(PATIENT_LIST_QUERY_KEYS.PAGE) || "",
    10
  );

  return {
    search,
    risk: VALID_RISK.has(riskValue) ? riskValue : FILTER_OPTIONS.ALL,
    age: VALID_AGE.has(ageValue) ? ageValue : FILTER_OPTIONS.ALL,
    gender: VALID_GENDER.has(genderValue) ? genderValue : FILTER_OPTIONS.ALL,
    sort: VALID_SORT_FIELDS.has(sortValue) ? sortValue : DEFAULT_PATIENT_SORT.field,
    dir: VALID_DIRECTIONS.has(dirValue as PatientListQuery["dir"])
      ? (dirValue as PatientListQuery["dir"])
      : DEFAULT_PATIENT_SORT.direction,
    page:
      Number.isFinite(pageRaw) && pageRaw > 0
        ? pageRaw
        : PAGINATION.DEFAULT_PAGE,
  };
};

const buildPatientListSearchParams = (query: PatientListQuery): URLSearchParams => {
  const params = new URLSearchParams();
  params.set(PATIENT_LIST_QUERY_KEYS.SEARCH, query.search);
  params.set(PATIENT_LIST_QUERY_KEYS.RISK, query.risk);
  params.set(PATIENT_LIST_QUERY_KEYS.AGE, query.age);
  params.set(PATIENT_LIST_QUERY_KEYS.GENDER, query.gender);
  params.set(PATIENT_LIST_QUERY_KEYS.SORT, query.sort);
  params.set(PATIENT_LIST_QUERY_KEYS.DIR, query.dir);
  params.set(PATIENT_LIST_QUERY_KEYS.PAGE, String(query.page));
  return params;
};

interface HomeProps {
  userEmail: string;
  isAdmin: boolean;
  userId: string;
}

export default function Home({ userEmail, isAdmin, userId }: HomeProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = useMemo(
    () => parsePatientListParams(searchParams),
    [searchParams]
  );

  const [searchDraft, setSearchDraft] = useState(query.search);
  const [patients, setPatients] = useState<Patient[]>([]);
  const [pagination, setPagination] = useState<PaginationState>({
    currentPage: query.page,
    totalPages: 1,
    recordsPerPage: PAGINATION.RECORDS_PER_PAGE,
    totalRecords: 0,
  });
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const initialLoadDoneRef = useRef(false);

  const updateListParams = useCallback(
    (patch: Partial<PatientListQuery>, replace = false) => {
      setSearchParams((prev) => {
        const current = parsePatientListParams(prev);
        const next = { ...current, ...patch };
        const shouldResetPage =
          patch.page === undefined &&
          (patch.search !== undefined ||
            patch.risk !== undefined ||
            patch.age !== undefined ||
            patch.gender !== undefined ||
            patch.sort !== undefined ||
            patch.dir !== undefined);
        if (shouldResetPage) {
          next.page = PAGINATION.DEFAULT_PAGE;
        }
        return buildPatientListSearchParams(next);
      }, { replace });
    },
    [setSearchParams]
  );

  useEffect(() => {
    const next = buildPatientListSearchParams(query);
    if (searchParams.toString() !== next.toString()) {
      setSearchParams(next, { replace: true });
    }
  }, [query, searchParams, setSearchParams]);

  useEffect(() => {
    setSearchDraft(query.search);
  }, [query.search]);

  useEffect(() => {
    if (searchDraft === query.search) {
      return;
    }
    const timeoutId = setTimeout(() => {
      updateListParams({ search: searchDraft }, true);
    }, PATIENT_LIST_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timeoutId);
  }, [searchDraft, query.search, updateListParams]);

  const fetchPatients = useCallback(async () => {
    if (initialLoadDoneRef.current) {
      setIsRefreshing(true);
    } else {
      setLoading(true);
    }
    setError(null);

    const filters: Record<string, string> = {};
    if (query.gender !== FILTER_OPTIONS.ALL) {
      filters.gender = query.gender;
    }
    if (query.risk !== FILTER_OPTIONS.ALL) {
      filters.risk_level = query.risk;
    }
    if (query.age !== FILTER_OPTIONS.ALL) {
      filters.age_group = query.age;
    }

    try {
      const requestOptions = apiUtils.createRequestOptions("POST", {
        page: query.page,
        limit: PAGINATION.RECORDS_PER_PAGE,
        ...(query.search ? { search: query.search } : {}),
        sort: {
          field: query.sort,
          direction: query.dir,
        },
        filters,
      });
      const response = await apiUtils.apiFetch(PATIENTS_LIST, requestOptions);

      const result = await apiUtils.handleResponse<{
        data: any[];
        page?: number;
        limit?: number;
        total?: number;
        totalPages?: number;
      }>(response);

      const mappedPatients: Patient[] = (result.data || []).map((p: any) => ({
        userId: p.userId,
        personal_information: p.personal_information,
        physical_details: p.physical_details,
        contact_information: p.contact_information,
        score_summary: p.score_summary,
        vitals: p.vitals,
        health_history: p.healthQuestionnaireResponse,
        hospital_information: p.hospital_information,
        hospital_id: p.hospital_id,
        created_at: p.created_at,
        updated_at: p.updated_at,
      }));

      const total = result.total ?? mappedPatients.length;
      const limit = result.limit ?? PAGINATION.RECORDS_PER_PAGE;
      const totalPages =
        result.totalPages ?? (limit > 0 ? Math.ceil(total / limit) : 0);

      setPatients(mappedPatients);
      setPagination({
        currentPage: result.page ?? query.page,
        totalPages,
        recordsPerPage: limit,
        totalRecords: total,
      });
      setError(null);
    } catch (error: any) {
      const errorMessage = errorUtils.getErrorMessage(error);

      // Expired/invalid sessions are handled globally (session cleared and
      // redirect to Login triggered in apiUtils.handleResponse), so no need to
      // surface an error here.
      setError(
        errorMessage === "Failed to fetch patients"
          ? MESSAGES.ERRORS.FETCH_PATIENTS
          : errorMessage
      );
      errorUtils.logError(error, "Home - fetchPatients");
    } finally {
      initialLoadDoneRef.current = true;
      setLoading(false);
      setIsRefreshing(false);
    }
  }, [
    query.page,
    query.search,
    query.risk,
    query.age,
    query.gender,
    query.sort,
    query.dir,
  ]);

  useEffect(() => {
    fetchPatients();
  }, [fetchPatients]);

  const handleSearch = useCallback((value: string) => {
    setSearchDraft(value);
  }, []);

  const handleRiskLevelChange = useCallback(
    (value: string) => {
      updateListParams({ risk: value });
    },
    [updateListParams]
  );

  const handleAgeGroupChange = useCallback(
    (value: string) => {
      updateListParams({ age: value });
    },
    [updateListParams]
  );

  const handleGenderChange = useCallback(
    (value: string) => {
      updateListParams({ gender: value });
    },
    [updateListParams]
  );

  const handleSortChange = useCallback(
    (next: { field: string; direction: "asc" | "desc" }) => {
      updateListParams({ sort: next.field, dir: next.direction });
    },
    [updateListParams]
  );

  const handlePageChange = useCallback(
    (page: number) => {
      updateListParams({ page });
    },
    [updateListParams]
  );

  const handleClearFilters = useCallback(() => {
    setSearchDraft("");
    updateListParams(
      {
        search: "",
        risk: FILTER_OPTIONS.ALL,
        age: FILTER_OPTIONS.ALL,
        gender: FILTER_OPTIONS.ALL,
        page: PAGINATION.DEFAULT_PAGE,
      },
      true
    );
  }, [updateListParams]);

  if (loading) {
    return (
      <PageLoading
        message={MESSAGES.LOADING.PATIENTS}
        fullScreen
        className="bg-guardian-10"
      />
    );
  }

  if (error && patients.length === 0) {
    return (
      <div className="bg-guardian-10 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 mb-4">
            <svg
              className="w-12 h-12 mx-auto mb-2"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <p className="font-medium">{error}</p>
          </div>
          <button
            onClick={fetchPatients}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 bg-guardian-10 min-h-full w-full max-w-full">
      <FilterComponent
        searchQuery={searchDraft}
        riskLevel={query.risk}
        ageGroup={query.age}
        gender={query.gender}
        onSearch={handleSearch}
        onRiskLevelChange={handleRiskLevelChange}
        onAgeGroupChange={handleAgeGroupChange}
        onGenderChange={handleGenderChange}
        onClearFilters={handleClearFilters}
      />

      <div className="px-3 md:px-4 pb-4 min-w-0">
        <DataTable
          data={patients}
          pagination={{
            ...pagination,
            currentPage: query.page,
          }}
          isRefreshing={isRefreshing}
          userEmail={userEmail}
          isAdmin={isAdmin}
          userId={userId}
          onPatientListRefresh={fetchPatients}
          sortState={{ field: query.sort, direction: query.dir }}
          onSortChange={handleSortChange}
          onPageChange={handlePageChange}
        />
      </div>
    </div>
  );
}
